import AlertMessageBase from 'components/Generic/AlertMessageBase';
import { useI18n } from 'next-localization';
import { useRouter } from 'next/router';
import { ComponentProps } from 'lib/component-props';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { useCallback, useEffect, useMemo } from 'react';
import { fundCentreActions } from '@/store/fundCentre';
import { useSelector, useDispatch } from 'react-redux';
import { getFundCentreData } from '@/store/fundCentre/selectors';
import {
  FundCentreTabDataRequestParams,
  useLazyGetFundCentreTabDataQuery,
  useLazyFetchSearchResultsQuery,
} from '@/store/fundCentre/services/fundCentreAPI';
import useHelpers from 'hooks/helpers';
import FundsListingHeading from 'components/Pages/FundsCenter/AllFunds/FundsListing/FundsListingHeading';
import FundsListingFooter from 'components/Pages/FundsCenter/AllFunds/FundsListing/FundsListingFooter';
import FundsTable from 'components/Pages/FundsCenter/AllFunds/FundsListing/FundsTable';
import Pagination from 'components/includes/Pagination';
import FundsFiltersSection from 'components/Pages/FundsCenter/AllFunds/FundsFilters/FundsFiltersSection';

type FundListingProps = Partial<ComponentProps> & {
  countryCode: string;
  investorType: string;
  literatureAuthorization: string;
  jurisdiction: string;
  site: string;
  itemsPerPage: number;
  withFilters?: boolean;
  withPagination?: boolean;
  filters: Array<any>;
  allFundsTabTabs: any;
};

const FundsListing = ({
  rendering,
  countryCode,
  jurisdiction,
  investorType,
  literatureAuthorization,
  site,
  withPagination = true,
  withFilters = true,
  filters,
  itemsPerPage = 20,
  allFundsTabTabs,
}: FundListingProps) => {
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const router = useRouter();
  const { isReady } = router;
  const { updateUrlParams, getErrorDictionary } = useHelpers(rendering);

  const {
    allFundsCount,
    fundsInFocusCount,
    currentPage,
    searchQuery,
    selectedDate,
    selectedTab,
    activeTabKey,
    searchByISIN,
    paginationMaxPages,
    paginationData,
  } = useSelector(getFundCentreData);
  const appliedFilters = useSelector(
    (state) => state.fundCentre.appliedFilters,
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );
  const dispatch = useDispatch();
  const { language } = sitecoreContext;

  const totalFunds = useMemo(() => {
    return activeTabKey === 0 ? allFundsCount : fundsInFocusCount;
  }, [activeTabKey, allFundsCount, fundsInFocusCount]);

  const showDatePicker = useMemo(() => {
    return sitecoreContext.site?.name !== 'invTrustsJss';
  }, [sitecoreContext.site?.name]);

  const payload: FundCentreTabDataRequestParams = useMemo(() => {
    return {
      countryInvestors: {
        countryCode,
        investorType,
        jurisdiction,
        literatureAuthorization,
      },
      searchQuery,
      language: language ?? null,
      site,
      skip: paginationData.skip,
      take: paginationData.take || 20,
      filters: appliedFilters,
      tab: selectedTab === 'overview' ? 'overview' : 'prices',
      date: selectedDate,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, searchQuery, paginationData, selectedTab, selectedDate]);

  const [fetchData, { data, isLoading, isFetching, error }] = useLazyGetFundCentreTabDataQuery();
  const [getFundByISIN] = useLazyFetchSearchResultsQuery();

  useEffect(() => {
    if (data) dispatch(fundCentreActions.setFundsData(data));
  }, [data, dispatch]);

  const updatePageNumber = useCallback(
    (currentPage: number) => {
      dispatch(fundCentreActions.setCurrentPage(currentPage));
    },
    [dispatch]
  );

  const onPageSelect = async (page: number) => {
    updatePageNumber(page);
    dispatch(
      fundCentreActions.setPaginationData({
        ...paginationData,
        skip: (+page - 1) * paginationData.take,
      })
    );
    updateUrlParams([
      {
        name: 'page',
        value: page,
      },
    ]);
  };

  useEffect(() => {
    // itemsPerPage value can be configured in Sitecore. 20 is default store value.
    if (itemsPerPage !== 20) {
      dispatch(
        fundCentreActions.setPaginationData({
          skip: 0,
          take: itemsPerPage,
        })
      );
    }
    if (searchByISIN) {
      const fetchDataByISIN = async () => {
        const { data } = await getFundByISIN({
          countryInvestors: {
            countryCode,
            investorType,
            jurisdiction,
            literatureAuthorization,
          },
          language,
          site,
          searchQuery: {
            isin: searchQuery.name,
          },
        });
        fetchData({
          ...payload,
          searchQuery: {
            name: (data?.products[0]?.name || payload?.searchQuery?.name) ?? '',
            id: data?.products[0]?.id || '',
          },
        });
      };
      fetchDataByISIN();
      if (data) dispatch(fundCentreActions.setFundsData(data));
    } else {
      fetchData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, payload]);

  useEffect(() => {
    dispatch(
      fundCentreActions.setShareclassMappings(
        sitecoreContext.fundCentreConfiguration?.defaultShareClassMappings || {}
      )
    );

    const { page } = updateUrlParams();

    updatePageNumber(+page ?? 1);
    const { table } = router.query;
    const selectedTab =
      allFundsTabTabs.map((tab) => tab.key).find((tab) => tab === table) ?? allFundsTabTabs[0].key;
    dispatch(fundCentreActions.setSelectedTab(selectedTab));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, sitecoreContext.fundCentreConfiguration?.defaultShareClassMappings]);

  useEffect(() => {
    if (selectedDate) {
      updatePageNumber(1);
      updateUrlParams([
        {
          name: 'page',
          value: 1,
        },
      ]);
    }
  }, [selectedDate]);

  return (
    <section className="default-section-padding">
      <div className="container">
        {error ? (
          <AlertMessageBase type="error" title={t(getErrorDictionary(error))} />
        ) : (
          <>
            {withFilters && isReady && (
              <FundsFiltersSection
                filters={filters}
                showDatePicker={showDatePicker}
                className="mb-4 mb-lg-5"
              />
            )}
            <div className="funds-listing">
              <FundsListingHeading
                isLoading={isFetching}
                take={paginationData.take}
                currentPage={currentPage}
                totalFunds={totalFunds}
                allFundsTabTabs={allFundsTabTabs}
              />
              <FundsTable isFetching={isFetching} />
              <FundsListingFooter
                take={paginationData.take}
                currentPage={currentPage}
                totalFunds={totalFunds}
                className="mt-3 mt-md-4"
                isLoading={isFetching}
              />
              {withPagination && (
                <Pagination
                  currentPage={currentPage}
                  maxPages={paginationMaxPages === -1 ? 0 : paginationMaxPages}
                  className="justify-content-center mt-4 mt-md-8"
                  isLoading={isFetching}
                  showSkeleton={isLoading}
                  onSelect={onPageSelect}
                />
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default FundsListing;
